import React from 'react';
import { motion } from 'framer-motion';
import type { Slide } from './slides';
const ease = [0.22, 1, 0.36, 1] as const;
const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    y: 18
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease
    }
  }
};
function Eyebrow({
  slide,
  index
}: {
  slide: Slide;
  index: number;
}) {
  const Icon = slide.icon;
  return <motion.div variants={item} className="flex items-center gap-3 text-accent" data-id="element-93" data-mp="2orgbksviwjzb">
      <span className="flex h-8 w-8 items-center justify-center rounded-md border border-accent/25 bg-accent/10" data-id="element-94" data-mp="2ko0fpyra2nno">
        <Icon className="h-4 w-4" strokeWidth={2.2} data-id="element-95" data-mp="2fonjwoifi68y" />
      </span>
      <span className="font-mono text-xs uppercase tracking-[0.25em]" data-id="element-96" data-mp="199d5dgaouj6p">
        {String(index + 1).padStart(2, '0')} · {slide.section}
      </span>
    </motion.div>;
}
export function SlideView({
  slide,
  index
}: {
  slide: Slide;
  index: number;
}) {
  const isTitle = slide.kind === 'title';
  return <motion.div key={slide.id} variants={container} initial="hidden" animate="show" exit={{
    opacity: 0,
    y: -12,
    transition: {
      duration: 0.25,
      ease
    }
  }} className="mx-auto flex w-full max-w-5xl flex-col justify-center px-6 py-16 sm:px-10 md:px-14" data-id="element-97" data-mp="1n4h4vc4mdvq8">
      {!slide.positioningPoints && !slide.competitiveTableRows && <Eyebrow slide={slide} index={index} data-id="element-98" data-mp="zdlkv16eo4tj" />}

      <div className={`${slide.positioningPoints || slide.competitiveTableRows ? '' : 'mt-8'} ${slide.portrait ? 'grid items-start gap-10 lg:grid-cols-[minmax(0,1fr)_270px]' : ''}`} data-id="element-99" data-mp="145le6aryzzh5">
        <div data-id="element-100" data-mp="qcnfofk8dr9d">
          {!slide.moatStages && !slide.velocityCards && !slide.positioningPoints && !slide.competitiveTableRows && <motion.h1 variants={item} className={`font-extrabold tracking-tight text-white ${slide.id === 'title' ? 'text-3xl leading-[1.15] sm:text-4xl lg:text-5xl' : isTitle ? 'text-5xl leading-[1.05] sm:text-6xl md:text-7xl' : 'text-4xl leading-[1.08] sm:text-5xl md:text-6xl'}`} data-id="element-101" data-mp="gg0rphwlyk34">
              {slide.title}
            </motion.h1>}

          {slide.lead && <motion.p variants={item} className="mt-6 max-w-3xl text-lg font-medium text-accent/90 sm:text-xl" data-id="element-102" data-mp="2ed0v4molylez">
              {slide.lead}
            </motion.p>}

          {slide.body.length > 0 && <motion.div variants={item} className="mt-6 max-w-3xl space-y-4 text-lg leading-relaxed text-slate-400 sm:text-xl" data-id="element-103" data-mp="321tsmnkvp1bp">
              {slide.body.map((p, i) => <p key={i} data-id="element-104" data-mp="2s45ox85aojhe" data-mpi={i}>{p}</p>)}
            </motion.div>}

          {slide.metrics && !slide.competitiveTableRows && <motion.div variants={item} className={`overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.06] ${slide.kind === 'appendix' ? 'mt-10 grid max-w-3xl grid-cols-1 gap-px sm:grid-cols-2' : 'mt-12 grid grid-cols-2 gap-px sm:grid-cols-4'}`} data-id="element-105" data-mp="1lovbk39aj2gq">
              {slide.metrics.map(m => <div key={m.label} className={`bg-panel2 ${slide.kind === 'appendix' ? 'px-5 py-5 sm:px-6' : 'px-5 py-7 sm:px-6 sm:py-8'}`} data-id="element-106" data-mp="1ggvi9znd3rnw">
                  {slide.kind === 'appendix' ? <>
                      <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-accent" data-id="element-107" data-mp="1z97urf7qc9br">
                        {m.label}
                      </div>
                      {m.href ? <a href={m.href} target={m.href.startsWith('http') ? '_blank' : undefined} rel={m.href.startsWith('http') ? 'noreferrer' : undefined} className="mt-2 block break-words text-base font-semibold leading-snug text-white underline decoration-accent/50 underline-offset-4 transition-colors hover:text-accent hover:decoration-accent sm:text-lg" data-id="element-108" data-mp="4v6mk3qai9z0">
                          {m.value}
                        </a> : <div className="mt-2 break-words text-base font-semibold leading-snug text-white sm:text-lg" data-id="element-109" data-mp="24aw0kptsqnu5">
                          {m.value}
                        </div>}
                    </> : <>
                      <div className="font-mono text-2xl font-semibold text-accent sm:text-3xl" data-id="element-110" data-mp="1qes9v6i4sxmf">
                        {m.value}
                      </div>
                      <div className="mt-2 text-sm leading-snug text-slate-400" data-id="element-111" data-mp="xwekzwerwlzh">
                        {m.label}
                      </div>
                    </>}
                </div>)}
            </motion.div>}

          {slide.mechanisms && <motion.div variants={item} className="mt-9 grid gap-3 md:grid-cols-3" aria-label="Defensible serving mechanisms" data-id="element-112" data-mp="39cf03kmz4bi0">
              {slide.mechanisms.map((mechanism, mechanismIndex) => <article key={mechanism.title} className="rounded-lg border border-white/[0.08] bg-panel2 p-4" data-id="element-113" data-mp="30jtiw8yzqcva" data-mpi={mechanismIndex}>
                  <span className="font-mono text-xs font-semibold text-accent" data-id="element-114" data-mp="3s9pxt0syiwvc">
                    0{mechanismIndex + 1}
                  </span>
                  <h2 className="mt-2 text-sm font-bold text-white" data-id="element-115" data-mp="3edcgbrofavgv">
                    {mechanism.title}
                  </h2>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-400" data-id="element-116" data-mp="22va1t1fp72d9">
                    {mechanism.body}
                  </p>
                </article>)}
            </motion.div>}

          {slide.moatStages && <motion.section variants={item} className="mt-1" aria-label="OpenFPGA model and silicon pipeline" data-id="element-117" data-mp="1ns05q7vjrg5l">
              <h1 className="max-w-6xl text-3xl font-extrabold leading-[1.08] tracking-tight text-slate-100 sm:text-4xl lg:text-5xl" data-id="element-118" data-mp="1jtatwjq5q6o2">
                {slide.moatHeadline}
              </h1>
              {slide.moatDescription && <p className="mt-4 max-w-5xl text-base leading-relaxed text-slate-400 sm:text-lg" data-id="element-119" data-mp="152tkoe7a6cgm">
                  {slide.moatDescription}
                </p>}

              <div className="mt-6 grid gap-2 lg:grid-cols-[minmax(0,1fr)_20px_minmax(0,1fr)_20px_minmax(0,1fr)_20px_minmax(0,1fr)]" data-id="element-120" data-mp="w1lygui1h22w">
                {slide.moatStages.map((stage, stageIndex) => <React.Fragment key={stage.number} data-id="element-121" data-mp="2qfmvraxxxh0v" data-mpi={stageIndex}>
                    <article className={`min-h-[150px] rounded-xl border bg-panel2 p-4 ${stageIndex === slide.moatStages!.length - 1 ? 'border-accent/80 bg-accent/[0.06]' : 'border-slate-700/80'}`} data-id="element-122" data-mp="2qebq15kxpz74">
                      <div className="font-mono text-[11px] font-medium tracking-[0.14em] text-accent" data-id="element-123" data-mp="2sz9pko35bndy">{stage.number} · {stage.title}</div>
                      <h2 className="mt-3 text-lg font-bold leading-tight text-slate-100 sm:text-xl" data-id="element-124" data-mp="2xwt9o4miyudt">{stage.detail.split('\n')[0]}</h2>
                      {stage.detail.split('\n')[1] && <p className="mt-3 text-sm leading-relaxed text-slate-400 sm:text-base" data-id="element-125" data-mp="37qs6co3txlyd">{stage.detail.split('\n')[1]}</p>}
                    </article>
                    {stageIndex < slide.moatStages!.length - 1 && <span aria-hidden className="hidden self-center text-center text-lg font-semibold text-accent lg:block" data-id="element-126" data-mp="2jyblk5spjfvd">→</span>}
                  </React.Fragment>)}
              </div>

              {slide.moatStats && <div className="mt-5 flex flex-wrap gap-2 font-mono text-xs leading-relaxed text-slate-200 sm:text-sm" data-id="element-127" data-mp="3ah970lo8di1">
                  {slide.moatStats.map((stat, statIndex) => <span key={stat.value} className="rounded-full border border-slate-700 bg-panel2 px-3 py-2" data-id="element-128" data-mp="p9ya4c8yalc3" data-mpi={statIndex}>
                      <span className={statIndex === 2 ? 'text-slate-200' : 'font-semibold text-accent'} data-id="element-129" data-mp="z1ubpxa8wtl2">{stat.value}</span>{' '}
                      <span className={statIndex === 2 || statIndex === 3 ? 'text-accent font-semibold' : ''} data-id="element-130" data-mp="uacla35umbec">{stat.label}</span>
                    </span>)}
                </div>}

              {slide.moatConclusion && <p className="mt-5 rounded-xl border border-slate-700 border-l-4 border-l-accent bg-panel2 px-5 py-5 text-lg font-bold leading-snug text-slate-100 sm:text-xl" data-id="element-131" data-mp="13625i8vk8zq5">
                  {slide.moatConclusion}
                </p>}
            </motion.section>}

          {slide.positioningPoints && <motion.figure variants={item} className="mt-1" aria-labelledby="positioning-map-title" data-id="element-132" data-mp="3eu0bx9aca0la">
              <p className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-accent" data-id="element-133" data-mp="1uaxx65v8yctb">
                {String(index + 1).padStart(2, '0')} · Appendix
              </p>
              <h1 id="positioning-map-title" className="mt-4 text-3xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-4xl lg:text-5xl" data-id="element-134" data-mp="1klo70vjyr47u">
                {slide.title}
              </h1>

              <div className="mt-7 overflow-x-auto pb-12 pl-28" data-id="element-135" data-mp="1bk6s3gial8kq">
                <div className="relative min-w-[680px] border-b-2 border-l-2 border-slate-600" style={{
              height: '430px'
            }} data-id="element-136" data-mp="1bmsvd3y5h1x5">
                  <div className="absolute left-1/2 top-0 h-full border-l border-dashed border-slate-600/70" data-id="element-137" data-mp="11msc1i0a6lsy" />
                  <div className="absolute left-0 top-1/2 w-full border-t border-dashed border-slate-600/70" data-id="element-138" data-mp="16l3l3qir4sa4" />
                  <div className="absolute right-[2%] top-[4%] h-[43%] w-[46%] rounded-2xl border-2 border-dashed border-accent/70" data-id="element-139" data-mp="2w09yf1oy9cme" />

                  <div className="absolute -left-[96px] top-0 text-right" data-id="element-140" data-mp="30yv25b3euefl">
                    <p className="text-base font-bold text-white" data-id="element-141" data-mp="2zihw1nmk3huh">Lockstep</p>
                    <p className="mt-1 text-xs text-slate-400" data-id="element-142" data-mp="34a9ok3p9vxn9">p99 = p50</p>
                  </div>
                  <p className="absolute -bottom-8 left-0 text-sm font-bold text-white" data-id="element-143" data-mp="2qjeb0e3twsdc">Frozen silicon</p>
                  <p className="absolute -bottom-8 right-0 text-sm font-bold text-white" data-id="element-144" data-mp="2vgwjy4huj7lc">Reconfigurable datapath</p>
                  <p className="absolute -left-[96px] bottom-2 text-base font-bold text-white" data-id="element-145" data-mp="3j8lja4t34wbs">Best-effort</p>

                  {slide.positioningPoints.map(point => <div key={point.name} className={`absolute -translate-x-1/2 -translate-y-1/2 ${point.featured ? 'z-10' : ''}`} style={{
                left: `${point.x}%`,
                top: `${point.y}%`
              }} data-id="element-146" data-mp="399mdctzpfesh">
                      <span className={`absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full ${point.featured ? 'bg-accent ring-4 ring-accent/15' : 'bg-slate-500'}`} data-id="element-147" data-mp="22t0sbe74w5vf" />
                      <div className={`w-40 ${point.x > 60 ? '-translate-x-[calc(100%+1.25rem)] text-right' : 'translate-x-6 text-left'}`} data-id="element-148" data-mp="1v7okccjet5q1">
                        <p className={`text-sm font-bold ${point.featured ? 'text-accent' : 'text-white'}`} data-id="element-149" data-mp="249xrqtjdsiz7">{point.name}</p>
                        <p className={`mt-0.5 whitespace-pre-line text-xs leading-snug ${point.featured ? 'text-accent/80' : 'text-slate-400'}`} data-id="element-150" data-mp="1zc626667l8y6">{point.description}</p>
                      </div>
                    </div>)}
                </div>
              </div>

              <figcaption className="mt-1 max-w-4xl text-base leading-relaxed text-slate-400 sm:text-lg" data-id="element-151" data-mp="1lbvax20zlsch">
                Hardware flexibility: hours + $0 NRE vs years + $50-150M respin
              </figcaption>

              <ul className="sr-only" data-id="element-152" data-mp="su9563a1hlyx">
                {slide.positioningPoints.map(point => <li key={point.name} data-id="element-153" data-mp="nw7qdujnc8t9">{point.name}: {point.description}</li>)}
              </ul>
            </motion.figure>}

          {slide.velocityCards && <motion.section variants={item} className="mt-1" aria-label="Thirty-day velocity" data-id="element-154" data-mp="2nwcw2z4rtabe">
              <p className="font-mono text-xs font-medium uppercase tracking-[0.3em] text-accent" data-id="element-155" data-mp="2sv7g24eh54x3">
                {slide.velocityHeadline}
              </p>
              <h1 className="mt-3 text-3xl font-extrabold leading-[1.08] tracking-tight text-slate-100 sm:text-4xl lg:text-5xl" data-id="element-156" data-mp="32m20d385tufw">
                30-Day Velocity
              </h1>
              {slide.velocityDescription && <p className="mt-4 max-w-5xl text-base leading-relaxed text-slate-400 sm:text-lg" data-id="element-157" data-mp="29yoafp2vpz5a">
                  {slide.velocityDescription}
                </p>}

              <div className="mt-6 grid gap-2 md:grid-cols-2 lg:grid-cols-4" data-id="element-158" data-mp="2izvzrhnf3pn8">
                {slide.velocityCards.map(card => <article key={card.value} className={`min-h-[126px] rounded-xl border border-slate-700/80 bg-panel2 p-3 ${card.className ?? ''}`} data-id="element-159" data-mp="3qf0mgwksx4xo">
                    <p className="font-mono text-2xl font-semibold tracking-tight text-accent sm:text-3xl" data-id="element-160" data-mp="1epaxtpl3k2pr">
                      {card.value}
                    </p>
                    <p className="mt-2 text-xs leading-relaxed text-slate-400 sm:text-sm" data-id="element-161" data-mp="19wrlevy5bdmj">{card.detail}</p>
                  </article>)}
              </div>

              {slide.velocityChips && <div className="mt-4 flex flex-wrap gap-2 font-mono text-xs leading-relaxed sm:text-sm" data-id="element-162" data-mp="zybtiqrao0bz">
                  {slide.velocityChips.map(chip => <span key={chip.prefix} className="rounded-xl border border-slate-700 bg-panel2 px-3 py-2 text-slate-400" data-id="element-163" data-mp="r0jc9h20iily">
                      <span className="font-semibold text-accent" data-id="element-164" data-mp="m2hwp63sp1f8">{chip.prefix}</span>{chip.suffix}
                    </span>)}
                </div>}
            </motion.section>}

          {slide.competitiveTableRows && <motion.section variants={item} className="mt-1" aria-label="Competitive positioning" data-id="element-165" data-mp="1d4tvp0ycqp2s">
              <p className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-accent" data-id="element-166" data-mp="1i1snvdygglyc">
                {String(index + 1).padStart(2, '0')} · Appendix
              </p>
              <h1 className="mt-4 text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl md:text-6xl" data-id="element-167" data-mp="191e087y0quro">
                {slide.title}
              </h1>
              <div className="mt-7 overflow-x-auto rounded-xl border border-white/[0.08]" data-id="element-168" data-mp="z3zkybnu0c0y">
                <table className="w-full min-w-[760px] border-collapse text-left" data-id="element-169" data-mp="z5u35i7pxq1c">
                  <thead className="bg-panel" data-id="element-170" data-mp="lai1eqv5azax">
                    <tr className="border-b border-white/[0.08]" data-id="element-171" data-mp="pdep2vzjlllq">
                      {['Approach', 'Decode efficiency', 'Deterministic latency', 'New model onto the chip'].map(heading => <th key={heading} scope="col" className="px-4 py-3 font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400" data-id="element-172" data-mp="gdrlc3jcc3pi">
                          {heading}
                        </th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.06] bg-panel2" data-id="element-173" data-mp="1q9otsfxmfe6">
                    {slide.competitiveTableRows.map(row => <tr key={row.approach} className={row.approach === 'OpenFPGA' ? 'bg-accent/[0.06]' : ''} data-id="element-174" data-mp="for91g6mp9rc">
                        <td className="px-4 py-3 align-top" data-id="element-175" data-mp="apelsubui0o8">
                          <div className={`text-sm font-semibold ${row.approach === 'OpenFPGA' ? 'text-accent' : 'text-white'}`} data-id="element-176" data-mp="pfc4wvux406k">{row.approach}</div>
                          <div className="mt-0.5 text-xs leading-snug text-slate-500" data-id="element-177" data-mp="kjn3emzc897m">{row.approachDetail}</div>
                        </td>
                        <td className="px-4 py-3 align-top" data-id="element-178" data-mp="ug9o9brnz09n">
                          <div className="text-sm font-medium text-slate-200" data-id="element-179" data-mp="11iok1f77et3v">{row.decodeEfficiency}</div>
                          {row.decodeDetail && <div className="mt-0.5 text-xs leading-snug text-slate-500" data-id="element-180" data-mp="16h83n8ybrst2">{row.decodeDetail}</div>}
                        </td>
                        <td className="px-4 py-3 align-top" data-id="element-181" data-mp="1bf9mptvr3s4j">
                          <div className="text-sm font-medium text-slate-200" data-id="element-182" data-mp="1g7t1uva36vbg">{row.deterministicLatency}</div>
                          {row.deterministicDetail && <div className="mt-0.5 text-xs leading-snug text-slate-500" data-id="element-183" data-mp="1k8whphs4wrco">{row.deterministicDetail}</div>}
                        </td>
                        <td className="px-4 py-3 align-top" data-id="element-184" data-mp="1u7lskqlm5bjf">
                          <div className="text-sm font-medium text-slate-200" data-id="element-185" data-mp="1u59amdfuv16d">{row.modelChange}</div>
                          {row.modelChangeDetail && <div className="mt-0.5 text-xs leading-snug text-slate-500" data-id="element-186" data-mp="3vyp4rkb5iy63">{row.modelChangeDetail}</div>}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              <p className="mt-5 text-lg font-medium leading-relaxed text-accent sm:text-xl" data-id="element-187" data-mp="2i3ysr1uiry92">
                The only deterministic inference that isn't frozen into silicon.
              </p>

              {slide.proof && <p className="mt-5 max-w-full overflow-x-auto whitespace-nowrap border-t border-white/[0.08] pt-4 text-base font-medium text-slate-300 sm:text-lg" data-id="element-188" data-mp="297zodp6g1wzh">
                  {slide.proof}
                </p>}
            </motion.section>}

          {slide.comparisonRows && <motion.div variants={item} className="mt-8 overflow-x-auto rounded-xl border border-white/[0.08]" data-id="element-189" data-mp="2s0lcfnb40j9i">
              <table className="w-full min-w-[640px] border-collapse text-left" aria-label="GPU-serving pain points and FPGA answers" data-id="element-190" data-mp="2n18pl2k8nc6x">
                <thead className="bg-panel" data-id="element-191" data-mp="1bmleghvtr3ow">
                  <tr className="border-b border-white/[0.08]" data-id="element-192" data-mp="1blk6wne0ozbx">
                    <th scope="col" className="w-1/2 px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400" data-id="element-193" data-mp="3bp4flwfjjjf4">
                      GPU-serving pain
                    </th>
                    <th scope="col" className="w-1/2 px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-accent" data-id="element-194" data-mp="31r6tpfl5lypo">
                      Our FPGA answer
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06] bg-panel2" data-id="element-195" data-mp="3ug4crkx6vzjh">
                  {slide.comparisonRows.map(row => <tr key={row.pain} data-id="element-196" data-mp="3kpj392fz9als">
                      <td className="px-5 py-3.5 text-sm leading-relaxed text-slate-300" data-id="element-197" data-mp="3kohvb6u9d48a">
                        {row.pain}
                      </td>
                      <td className="px-5 py-3.5 text-sm font-medium leading-relaxed text-accent/90" data-id="element-198" data-mp="3pm9n80uwkqch">
                        {row.answer}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </motion.div>}

          {slide.footer && !slide.moatStages && !slide.competitiveTableRows && <motion.p variants={item} className="mt-6 border-t border-white/[0.08] pt-4 font-mono text-[11px] leading-relaxed text-slate-500" data-id="element-199" data-mp="21ql82l5yeixi">
              {slide.footer}
            </motion.p>}
        </div>

        {slide.portrait && <motion.figure variants={item} className="mx-auto mt-2 w-full max-w-[270px] lg:mx-0" data-id="element-200" data-mp="1nrlt6588tiej">
            <div className="overflow-hidden rounded-xl border border-accent/25 bg-panel2 p-1.5 shadow-2xl shadow-black/30" data-id="element-201" data-mp="1itk2ay4ksesi">
              <img src={slide.portrait.src} alt={slide.portrait.alt} className="aspect-[4/5] w-full rounded-lg object-cover object-center" data-id="element-202" data-mp="1wpfdehsqgllc" />
            </div>
            <figcaption className="mt-3 font-mono text-xs uppercase tracking-[0.18em] text-slate-500" data-id="element-203" data-mp="345lsbdz9l40q">
              Orion Parrott · Founder
            </figcaption>
          </motion.figure>}
      </div>
    </motion.div>;
}